// src/components/modals/AddProjectObjectiveModal.tsx
import React, { useState } from "react";
import { useProjectStore } from "../../stores/projectStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "../ui/dialog";
import { FlagIcon } from "@heroicons/react/24/solid";
import { Plus } from "lucide-react";
import { useGetAllProjects, useUpdateProject } from "@/api/ProjetsApi";
import SmallLoader from "../SmallLoader";

const AddProjectObjectiveModal: React.FC = () => {
  const [newObjectives, setNewObjectives] = useState<string[]>([]); // Local state for new objectives
  const [objectiveInput, setObjectiveInput] = useState<string>(""); // State for input value
  const [open, setOpen] = useState(false);
  const { selectedProject } = useProjectStore(); // Access project and addFiles from the store
  const { updateProjectObjectives, loading } = useUpdateProject();
  const { getAllProjects } = useGetAllProjects();

  const handleAddProjectObjective = async (objectives: string[]) => {
    if (!selectedProject?.id) {
      console.error("No project selected!");
      return;
    }

    if (newObjectives.length === 0) {
      console.error("No objectives to add!");
      return;
    }

    const updateSuccess = await updateProjectObjectives({
      ...selectedProject,
      objectives: objectives.join(","),
    }); // Call the store function

    if (updateSuccess) {
      setNewObjectives([]); // Reset the local objectives state
      setOpen(false); // Close the modal}
      getAllProjects(); // Fetch all projects again
    }
  };
  const handleSetNewProjects = () => {
    if (objectiveInput.trim()) {
      setNewObjectives((prev) => [...prev, objectiveInput.trim()]); // Add input value to objectives
      setObjectiveInput(""); // Clear input
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setObjectiveInput(e.target.value); // Update input state
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && objectiveInput.trim()) {
      handleSetNewProjects(); // Add objective on Enter
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-rajapiNavyBlue rounded-lg text-white py-1 hover:shadow-md">
          Add Objectives
        </button>
      </DialogTrigger>
      <DialogContent className="w-full max-w-xl p-8">
        <DialogHeader>
          <div className="w-full flex">
            <div className="bg-white p-4 rounded-2xl w-fit">
              <FlagIcon className="w-6 h-6 text-rajapiBlue" />
            </div>
            {loading && <SmallLoader modal={true} />}
          </div>
        </DialogHeader>
        <div className="mt-4">
          <h2 className="text-xl font-medium text-black text-blue-950">
            Définir les objectifs du projet
          </h2>
          <h2 className="text-xs font-medium text-gray">
            Start create and manage your project by few clicks!
          </h2>
          <div className="border-white border-2 my-2 mt-6 px-3 py-4  bg-inherit rounded-2xl">
            <label
              htmlFor="add-objective"
              className="block text-sm font-medium"
            >
              Objectif #{newObjectives.length + 1}
            </label>
            <input
              id="add-objective"
              type="text"
              value={objectiveInput}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              className="w-full bg-inherit font-light	outline-none text-xs	"
              placeholder="Inscrivez le l'objectif"
            />
          </div>
          <button
            onClick={handleSetNewProjects}
            className="mt-2 w-full bg-inherit flex space-x-2 text-blue-950"
            disabled={!objectiveInput.trim()}
          >
            <Plus className="w-6 h-6" />
            <span>Add Objective</span>
          </button>
        </div>
        {newObjectives.length > 0 && (
          <ul className="mt-4 space-y-2">
            {newObjectives.map((objective, index) => (
              <li
                key={index}
                className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-md"
              >
                <div>
                  <p className="text-sm">{`Objective #${index + 1}`}</p>
                  <p className="text-xs text-gray-500">{objective}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-center">
          <button
            className="mr-2 bg-rajapiBlue py-4 px-20 rounded-xl text-white text-sm"
            type="submit"
            onClick={() => {
              handleAddProjectObjective(newObjectives);
            }}
            disabled={
              newObjectives.length === 0 || objectiveInput.trim().length > 0
            }
          >
            Continue
          </button>
          <button
            className="mr-2 bg-white py-4 px-20 rounded-xl text-blue-950 text-sm"
            onClick={() => {
              setOpen(false);
              setNewObjectives([]);
            }}
          >
            Annuler
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddProjectObjectiveModal;
